/* eslint-disable tap/no-raw-text-js */

const MAX_REQUESTS_PER_SESSION = 500;
const MAX_PAYLOAD_CHARS = 2000;
const MAX_WIDGET_INFO_PER_ENDPOINT = 10;
const DEFAULT_SLOW_REQUEST_THRESHOLD_MS = 15000;
const STATUS_UNKNOWN = 'unknown';
const STATUS_CANCELED = 'canceled';

const activeSessions = new Map();
let sessionSequence = 0;
let requestSequence = 0;

const nowPerf = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const nowEpoch = () => Date.now();
const toIso = (epochMs) => new Date(epochMs).toISOString();

const buildSessionId = () => {
  const id = `dash_perf_${nowEpoch()}_${sessionSequence}`;
  sessionSequence += 1;
  return id;
};
const buildRequestId = () => {
  const id = `req_${nowEpoch()}_${requestSequence}`;
  requestSequence += 1;
  return id;
};

const isCanceledError = (error) =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.message === 'canceled';

const normalizeMethod = (method) => (method ? method.toUpperCase() : 'GET');

const normalizeUrl = (config = {}, source = null) => {
  const baseURL = config.baseURL || '';
  const url = config.url || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  let fullUrl = url;
  try {
    fullUrl = new URL(url, baseURL || origin).toString();
  } catch {
    // Best effort only; keep original string.
  }

  let endpoint = url;
  let pathname = url;
  try {
    const parsedUrl = new URL(fullUrl, origin);
    endpoint = parsedUrl.pathname;
    pathname = parsedUrl.pathname;
  } catch {
    // ignore
  }

  const baseCategory = (() => {
    if (source === 'workerAxios') return 'worker';
    if (baseURL.includes('/server/api') || pathname.startsWith('/server/api')) return 'api';
    if (baseURL.includes('/app/dash') || pathname.startsWith('/app/dash')) return 'legacy_dash';
    if (baseURL.includes('/app/iotool') || pathname.startsWith('/app/iotool'))
      return 'legacy_iotool';
    return 'external';
  })();

  return {
    fullUrl,
    endpoint,
    baseCategory,
  };
};

const safeSerializePayload = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const keys = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const key of value.keys()) {
      keys.push(key);
      if (keys.length >= 50) break;
    }
    return JSON.stringify({ form_data_keys: keys });
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return JSON.stringify({ blob: true, size: value.size, type: value.type });
  }
  if (typeof value === 'string') {
    return value.length > MAX_PAYLOAD_CHARS ? `${value.slice(0, MAX_PAYLOAD_CHARS)}...` : value;
  }
  try {
    const serialized = JSON.stringify(value);
    if (serialized.length > MAX_PAYLOAD_CHARS) {
      return `${serialized.slice(0, MAX_PAYLOAD_CHARS)}...`;
    }
    return serialized;
  } catch {
    return '[unserializable]';
  }
};

const parseSerializedParams = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const stripRedundantWidgetParams = (params) => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return params;
  const cleaned = { ...params };
  delete cleaned.widget_request;
  delete cleaned.widget_type;
  delete cleaned.widget_page_id;
  return cleaned;
};

const addWidgetInfo = (entry, widget) => {
  if (!widget || !widget.id) return;
  if (!entry.widget_ids) {
    entry.widget_ids = new Set();
    entry.widget_types = new Set();
    entry.widget_titles = new Set();
  }

  if (entry.widget_ids.size < MAX_WIDGET_INFO_PER_ENDPOINT) {
    entry.widget_ids.add(String(widget.id));
  }
  if (widget.type && entry.widget_types.size < MAX_WIDGET_INFO_PER_ENDPOINT) {
    entry.widget_types.add(widget.type);
  }
  if (widget.title && entry.widget_titles.size < MAX_WIDGET_INFO_PER_ENDPOINT) {
    entry.widget_titles.add(widget.title);
  }
};

const buildTopEndpoints = (session) => {
  const endpointInfo = {};

  Object.entries(session.endpoint_counts).forEach(([endpointId, count]) => {
    const endpointLabel = session.endpoint_map?.[endpointId]?.endpoint ?? endpointId;
    endpointInfo[endpointId] = {
      endpoint_id: endpointId,
      endpoint: endpointLabel,
      count,
      widget_ids: new Set(),
      widget_types: new Set(),
      widget_titles: new Set(),
    };
  });

  session.requests.forEach((request) => {
    const endpointId = request.endpoint_id || request.endpoint || 'unknown';
    const endpointLabel =
      request.endpoint || session.endpoint_map?.[endpointId]?.endpoint || String(endpointId);

    if (!endpointInfo[endpointId]) {
      endpointInfo[endpointId] = {
        endpoint_id: endpointId,
        endpoint: endpointLabel,
        count: session.endpoint_counts?.[endpointId] ?? 0,
        widget_ids: new Set(),
        widget_types: new Set(),
        widget_titles: new Set(),
      };
    }

    const widgetId = request.widget_id;
    if (widgetId && session.widget_map?.[widgetId]) {
      addWidgetInfo(endpointInfo[endpointId], session.widget_map[widgetId]);
    } else if (request.widget) {
      addWidgetInfo(endpointInfo[endpointId], request.widget);
    }
  });

  return Object.values(endpointInfo)
    .sort((a, b) => {
      const aCount = a.widget_ids?.size ? a.widget_ids.size : a.count;
      const bCount = b.widget_ids?.size ? b.widget_ids.size : b.count;
      return bCount - aCount;
    })
    .slice(0, 10)
    .map((entry) => ({
      endpoint_id: entry.endpoint_id,
      endpoint: entry.endpoint,
      count: entry.widget_ids?.size ? entry.widget_ids.size : entry.count,
      request_count: entry.count,
      unique_widget_count: entry.widget_ids?.size ? entry.widget_ids.size : 0,
      widget_ids: Array.from(entry.widget_ids || []),
      widget_types: Array.from(entry.widget_types || []),
      widget_titles: Array.from(entry.widget_titles || []),
    }));
};

const initializeSessionCounters = (session) => {
  session.request_count = 0;
  session.completed_count = 0;
  session.error_count = 0;
  session.canceled_count = 0;
  session.pending_count = 0;
  session.requests_by_method = {};
  session.requests_by_base = {};
  session.requests_by_status = {};
  session.requests_by_status_class = {};
  session.endpoint_counts = {};
  session.unique_endpoint_count = 0;
  session.dropped_request_count = 0;
  session.totals = {
    duration_ms_total: 0,
    duration_ms_max: 0,
    duration_ms_min: null,
  };
};

const addToCount = (bucket, key, increment = 1) => {
  if (!bucket[key]) {
    bucket[key] = 0;
  }
  bucket[key] += increment;
};

const ensureEndpointEntry = (session, endpoint, baseCategory, exampleUrl) => {
  if (!session._endpoint_lookup) {
    session._endpoint_lookup = new Map();
    session._endpoint_seq = 0;
  }
  const existing = session._endpoint_lookup.get(endpoint);
  if (existing) {
    return existing;
  }

  const endpointId = `e${session._endpoint_seq}`;
  session._endpoint_seq += 1;
  session._endpoint_lookup.set(endpoint, endpointId);
  if (!session.endpoint_map) {
    session.endpoint_map = {};
  }
  session.endpoint_map[endpointId] = {
    endpoint,
    base: baseCategory,
    example_url: exampleUrl || endpoint,
  };
  return endpointId;
};

const ensureWidgetEntry = (session, widget) => {
  if (!widget || !widget.id) return null;
  if (!session.widget_map) {
    session.widget_map = {};
  }
  const widgetId = String(widget.id);
  if (!session.widget_map[widgetId]) {
    session.widget_map[widgetId] = {
      id: widget.id,
      type: widget.type ?? null,
      title: widget.title ?? null,
      layout_id: widget.layout_id ?? null,
    };
  }
  return widgetId;
};

const createSession = ({ type, dashboardId = null, layoutId = null, label = null, meta = {} }) => {
  const startEpoch = nowEpoch();
  const session = {
    session_id: buildSessionId(),
    type,
    dashboard_id: dashboardId,
    layout_id: layoutId,
    label,
    route: typeof window !== 'undefined' ? window.location.href : null,
    started_at_epoch_ms: startEpoch,
    started_at_iso: toIso(startEpoch),
    ended_at_epoch_ms: null,
    ended_at_iso: null,
    duration_ms: null,
    meta: meta || {},
    summary: {},
    endpoint_map: {},
    widget_map: {},
    requests: [],
    _in_flight: new Map(),
    _endpoint_lookup: new Map(),
    _endpoint_seq: 0,
  };
  initializeSessionCounters(session);
  return session;
};

const finalizeSession = (session, summary = {}, meta = {}, reason = null) => {
  const endEpoch = nowEpoch();
  session.ended_at_epoch_ms = endEpoch;
  session.ended_at_iso = toIso(endEpoch);
  session.duration_ms = endEpoch - session.started_at_epoch_ms;
  session.pending_count = session._in_flight.size;
  session.summary = { ...session.summary, ...(summary || {}) };
  session.meta = { ...session.meta, ...(meta || {}) };
  if (reason) {
    session.meta.end_reason = reason;
  }

  session.top_endpoints = buildTopEndpoints(session);

  if (session.completed_count > 0) {
    session.avg_duration_ms = Math.round(
      session.totals.duration_ms_total / session.completed_count
    );
  } else {
    session.avg_duration_ms = null;
  }

  session.requests.forEach((request) => {
    if (!request.ended_at_epoch_ms) {
      request.status = request.status ?? STATUS_UNKNOWN;
      request.duration_ms = request.duration_ms ?? null;
      request.pending = true;
    }
  });
};

const buildLayoutReport = (session, payload, options = {}) => {
  const slowThresholdMs = options.slowThresholdMs ?? DEFAULT_SLOW_REQUEST_THRESHOLD_MS;
  const performanceData = payload?.performanceData || {};
  const dashboard = performanceData.dashboard || {};
  const layout = dashboard.layout || {};
  const dateRange = dashboard.dateRange || {};

  const completedRequests = (session.requests || []).filter(
    (request) => request.duration_ms !== null
  );
  const endpointIds = new Set();
  const requestItems = completedRequests.map((request) => {
    const endpointId = request.endpoint_id || request.endpoint || 'unknown';
    endpointIds.add(endpointId);
    return {
      endpointId,
      widgetId: request.widget_id ?? null,
      status: request.status ?? null,
      durationMs: request.duration_ms ?? null,
    };
  });

  const slowOrErrorParams = completedRequests
    .filter((request) => {
      const duration = request.duration_ms ?? 0;
      const { status } = request;
      const isErrorStatus = typeof status === 'number' && status >= 400;
      const isExplicitError = request.error === true;
      return duration >= slowThresholdMs || isErrorStatus || isExplicitError;
    })
    .map((request) => {
      const endpointId = request.endpoint_id || request.endpoint || 'unknown';
      endpointIds.add(endpointId);
      return {
        endpointId,
        widgetId: request.widget_id ?? null,
        durationMs: request.duration_ms ?? null,
        status: request.status ?? null,
        params: stripRedundantWidgetParams(parseSerializedParams(request.params)),
      };
    });

  const endpointMap = {};
  endpointIds.forEach((endpointId) => {
    const mapEntry = session.endpoint_map?.[endpointId];
    if (mapEntry) {
      endpointMap[endpointId] = {
        endpoint: mapEntry.endpoint,
        base: mapEntry.base,
      };
    } else {
      endpointMap[endpointId] = { endpoint: endpointId, base: 'unknown' };
    }
  });

  const report = {
    event: 'dashboard_layout_load',
    id: session.session_id,
    url: performanceData.url ?? session.route,
    dashboard: { id: String(dashboard.id ?? session.dashboard_id ?? '') || null },
    layout: {
      id: String(layout.id ?? session.layout_id ?? '') || null,
      widgetCount: layout.widgetCount ?? null,
      loadedCount: layout.loadedCount ?? null,
      errorCount: layout.errorCount ?? null,
      noDataCount: layout.noDataCount ?? null,
      cacheHitCount: layout.cacheHitCount ?? null,
      peakConcurrentLoading: layout.peakConcurrentLoading ?? null,
      timing: {
        loadTime: layout.loadTime ?? layout.loadTimeMs ?? null,
        firstData: layout.firstData ?? layout.firstDataMs ?? null,
        renderTime: layout.renderTime ?? layout.renderTimeMs ?? null,
        overhead: layout.overhead ?? layout.overheadMs ?? null,
        total: session.duration_ms ?? null,
      },
      widgets: layout.widgets ?? [],
    },
    requests: {
      slowThresholdMs: slowThresholdMs ?? null,
      endpointMap,
      items: requestItems,
      slowOrErrorParams,
    },
    dateRange: {
      spanDays: dateRange.spanDays ?? null,
      current: {
        start: dateRange.current_date_range?.start ?? null,
        end: dateRange.current_date_range?.end ?? null,
        relativeRange: dateRange.current_date_range?.relativeRange ?? null,
      },
    },
    filters: dashboard.filters ?? null,
    cpu: dashboard.cpu ?? null,
    memory: dashboard.memory
      ? {
          usedHeapStartMB: dashboard.memory.atStart?.usedHeapMB ?? null,
          usedHeapEndMB: dashboard.memory.atEnd?.usedHeapMB ?? null,
          deltaUsedHeapMB: dashboard.memory.deltaUsedHeapMB ?? null,
        }
      : null,
    network: dashboard.network ?? null,
    client: dashboard.client ?? null,
    ts: {
      startEpochMs: session.started_at_epoch_ms ?? null,
      endEpochMs: session.ended_at_epoch_ms ?? null,
    },
  };

  return report;
};

const attachToWindow = (service) => {
  if (typeof window === 'undefined') return;
  window.__dashboardPerfLogger = service;
};

const DashboardPerformanceLogger = {
  startSession({ type, dashboardId, layoutId, label, meta } = {}) {
    if (!type) return null;
    const session = createSession({ type, dashboardId, layoutId, label, meta });
    activeSessions.set(session.session_id, session);
    return session.session_id;
  },

  endSession(sessionId, { summary, meta, reason } = {}) {
    if (!sessionId) return null;
    const session = activeSessions.get(sessionId);
    if (!session) return null;
    finalizeSession(session, summary, meta, reason);
    activeSessions.delete(sessionId);
    return session;
  },

  endActiveSessionByType(
    type,
    { dashboardId = null, layoutId = null, summary, meta, reason } = {}
  ) {
    const session = [...activeSessions.values()].find((item) => {
      if (item.type !== type) return false;
      if (dashboardId !== null && item.dashboard_id !== dashboardId) return false;
      if (layoutId !== null && item.layout_id !== layoutId) return false;
      return true;
    });
    if (!session) return null;
    return DashboardPerformanceLogger.endSession(session.session_id, { summary, meta, reason });
  },

  hasActiveSessionOfType(type) {
    return [...activeSessions.values()].some((session) => session.type === type);
  },

  buildLayoutReportFromSession(session, payload, options = {}) {
    if (!session) return null;
    return buildLayoutReport(session, payload, options);
  },

  captureRequest(config, source = null) {
    if (!activeSessions.size || !config) return config;
    const sessionIds = [...activeSessions.keys()];
    if (!sessionIds.length) return config;

    const requestId = buildRequestId();
    const { endpoint, baseCategory, fullUrl } = normalizeUrl(config, source);
    const method = normalizeMethod(config.method);
    const startEpoch = nowEpoch();
    const startPerf = nowPerf();
    const params = safeSerializePayload(config.params);
    const data = safeSerializePayload(config.data);
    const widgetMeta = config._dashboard_widget
      ? {
          id: config._dashboard_widget.id ?? null,
          type: config._dashboard_widget.type ?? null,
          title: config._dashboard_widget.title ?? null,
          layout_id: config._dashboard_widget.layout_id ?? null,
        }
      : null;

    sessionIds.forEach((sessionId) => {
      const session = activeSessions.get(sessionId);
      if (!session) return;

      const endpointId = ensureEndpointEntry(session, endpoint, baseCategory, fullUrl);
      const widgetId = ensureWidgetEntry(session, widgetMeta);

      session.request_count += 1;
      addToCount(session.requests_by_method, method);
      addToCount(session.requests_by_base, baseCategory);

      if (!session.endpoint_counts[endpointId]) {
        session.unique_endpoint_count += 1;
      }
      addToCount(session.endpoint_counts, endpointId);

      const entry = {
        request_id: requestId,
        method,
        endpoint_id: endpointId,
        widget_id: widgetId,
        params,
        data,
        started_at_epoch_ms: startEpoch,
        started_at_iso: toIso(startEpoch),
        ended_at_epoch_ms: null,
        status: null,
        duration_ms: null,
      };

      if (session.requests.length < MAX_REQUESTS_PER_SESSION) {
        session.requests.push(entry);
        session._in_flight.set(requestId, entry);
      } else {
        session.dropped_request_count += 1;
        session._in_flight.set(requestId, entry);
      }
    });

    config._dashboard_perf = {
      requestId,
      sessionIds,
      startPerf,
    };

    return config;
  },

  captureResponse(response) {
    if (!response?.config?._dashboard_perf) return response;
    const perf = response.config._dashboard_perf;
    const endPerf = nowPerf();
    const status = response.status ?? null;
    DashboardPerformanceLogger._finalizeRequest(perf, status, endPerf, false, false, {});
    return response;
  },

  captureError(error) {
    if (!error?.config?._dashboard_perf) return error;
    const perf = error.config._dashboard_perf;
    const endPerf = nowPerf();
    const status = error?.response?.status ?? null;
    const canceled = isCanceledError(error);
    const errorMessage =
      error?.response?.data?.message || error?.response?.data?.error || error?.message || null;
    DashboardPerformanceLogger._finalizeRequest(perf, status, endPerf, true, canceled, {
      errorMessage,
    });
    return error;
  },

  _finalizeRequest(perf, status, endPerf, isError, isCanceled, details = {}) {
    const statusKey = status ?? STATUS_UNKNOWN;
    const statusClass = status ? `${Math.floor(status / 100)}xx` : STATUS_UNKNOWN;
    const durationMs = Math.round(endPerf - perf.startPerf);

    perf.sessionIds.forEach((sessionId) => {
      const session = activeSessions.get(sessionId);
      if (!session) return;

      const entry = session._in_flight.get(perf.requestId);
      if (entry) {
        entry.ended_at_epoch_ms = nowEpoch();
        entry.status = statusKey;
        entry.duration_ms = durationMs;
        if (isError) {
          entry.error = true;
          if (details.errorMessage) {
            entry.error_message = details.errorMessage;
          }
        }
        if (isCanceled) {
          entry.canceled = true;
          entry.status = STATUS_CANCELED;
        }
      }

      session.completed_count += 1;
      session.totals.duration_ms_total += durationMs;
      session.totals.duration_ms_max = Math.max(session.totals.duration_ms_max, durationMs);
      session.totals.duration_ms_min =
        session.totals.duration_ms_min === null
          ? durationMs
          : Math.min(session.totals.duration_ms_min, durationMs);

      addToCount(session.requests_by_status, statusKey);
      addToCount(session.requests_by_status_class, statusClass);

      if (isError) {
        session.error_count += 1;
      }
      if (isCanceled) {
        session.canceled_count += 1;
      }

      session._in_flight.delete(perf.requestId);
    });
  },

  // No local persistence or exports; payload is sent via performance_report.
};

attachToWindow(DashboardPerformanceLogger);

export default DashboardPerformanceLogger;
